/**
 * Triaxial calculations — frontend mirror of
 * `backend/src/calculation/triaxialCalculations.ts`.
 *
 * MUST stay in sync with the backend. Backend is the source of truth on save.
 * This mirror exists for live preview only — the Parameters tab's Initial-
 * Properties read-outs (bulk density, void ratio, S_r) and the Summary Table
 * use these to update as the operator types.
 *
 * Standards:
 *   - ASTM D4767-11 (CIU — consolidated undrained, cohesive soils)
 *   - ASTM D7181 (CID — consolidated drained)
 *
 * Unit convention:
 *   length    mm
 *   mass      g
 *   density   Mg/m³ (numerically identical to g/cm³ — report renders the
 *             g/cm³ label, DB stores Mg/m³ to match Atterberg/PSD)
 *   stress    kPa
 *   strain    percent (use εDecimal = εPct / 100 inside formulas that need it)
 *   time      s
 *   volume    mm³
 *
 * Module layout — this file owns the initial / consolidation / post-cons
 * surfaces (~340 LOC); the shear-side surfaces (corrections, shear row,
 * report summary, unit conversion) live in `triaxialCalcShear.ts` and are
 * re-exported below so the public API stays a single import path.
 */

// Re-export the shear-side surface so callers can import everything from
// `./computations` for live preview.
export {
  deviatorStressCorrection,
  computeShearRow,
  computeReportSummary,
  toReportUnits,
} from './computationsShear';
export type {
  CorrectionInputs,
  TriaxialTestType,
  ShearRowRaw,
  ShearRowComputed,
  ShearRowContext,
  ReportSummaryInput,
  ReportSummaryOutput,
  ReportUnitsInput,
  ReportUnitsOutput,
} from './computationsShear';

// ============================================================================
// Constants & helpers
// ============================================================================

/** Water density: 1 g/cm³ = 0.001 g/mm³ = 1 Mg/m³ */
const WATER_DENSITY_G_PER_MM3 = 1e-3;

function avg(values: Array<number | null | undefined>): number | null {
  const filtered = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (filtered.length === 0) return null;
  return filtered.reduce((s, v) => s + v, 0) / filtered.length;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function nz(v: number | null | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function safe(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** g/mm³ → Mg/m³ (multiply by 1000) */
function gPerMm3ToMgPerM3(rho: number): number {
  return rho * 1000;
}

// ============================================================================
// 1. Initial properties
// ============================================================================

export interface InitialPropertiesInput {
  diameters: Array<number | null>;        // mm, up to 6
  lengths: Array<number | null>;          // mm, up to 6
  perimeterMm: number | null;             // optional; if set, drives calcDiameter
  /** Optional legacy override; new flow derives wet mass from former+wet+former pair. */
  massOfWetSampleG: number | null;
  massOfPanG: number | null;              // "former" mass (tare on the moulding former)
  massWetSamplePlusPanG: number | null;   // initial wet mass + former
  /** Legacy fields — pre-BUG-2026-0053. Retained so old rows still render. */
  massDrySamplePlusPanG: number | null;
  massExtraG: number | null;
  specificGravityGs: number | null;
  initialVolumeOverrideMm3: number | null;
  /**
   * Final dry mass (g) from the pan-based moisture sample (BUG-2026-0040,
   * single-reading per BUG-2026-0054). Drives initial water content and
   * initial dry density per the lab spreadsheet — BUG-2026-0053. Optional:
   * when null, falls back to the legacy dry-from-pan inputs above so
   * previously-saved rows still compute.
   */
  finalDryMassG: number | null;
}

export interface InitialPropertiesOutput {
  avgDiameterMm: number | null;
  avgLengthMm: number | null;
  calcDiameterMm: number | null;
  reportedDiameterMm: number | null;
  initialVolumeMm3: number | null;
  /** Computed: massWetSamplePlusPanG − massOfPanG, falling back to massOfWetSampleG. */
  initialWetMassG: number | null;
  bulkDensityMgPerM3: number | null;
  dryDensityMgPerM3: number | null;
  waterContentPct: number | null;
  voidRatio: number | null;
  degreeOfSatPct: number | null;
  /** Convenience: dry mass used for downstream post-cons / final calcs. */
  massDryG: number | null;
}

export function computeInitialProperties(p: InitialPropertiesInput): InitialPropertiesOutput {
  const avgD = avg(p.diameters);
  const avgL = avg(p.lengths);
  const calcD = p.perimeterMm !== null && p.perimeterMm > 0 ? p.perimeterMm / Math.PI : null;
  const reportedD = avgD ?? calcD;

  let V: number | null = null;
  if (p.initialVolumeOverrideMm3 !== null && p.initialVolumeOverrideMm3 > 0) {
    V = p.initialVolumeOverrideMm3;
  } else if (reportedD !== null && avgL !== null) {
    V = (Math.PI / 4) * reportedD * reportedD * avgL;
  }

  // Initial wet mass = WetSamplePlusPan − Pan ("former"). Falls back to the
  // legacy direct input when the pan pair isn't filled in.
  let initialWetMass: number | null = null;
  if (typeof p.massWetSamplePlusPanG === 'number' && typeof p.massOfPanG === 'number') {
    const derived = p.massWetSamplePlusPanG - p.massOfPanG;
    if (derived > 0) initialWetMass = derived;
  }
  if (initialWetMass === null && typeof p.massOfWetSampleG === 'number' && p.massOfWetSampleG > 0) {
    initialWetMass = p.massOfWetSampleG;
  }

  // Dry mass — BUG-2026-0053: prefer the final dry mass (pan-based) over the
  // legacy initial dry-from-pan back-calc. The lab no longer collects a
  // separate initial dry weighing; the post-test pan sample IS the dry mass.
  let massDry: number | null = null;
  if (typeof p.finalDryMassG === 'number' && p.finalDryMassG > 0) {
    massDry = p.finalDryMassG;
  } else if (
    typeof p.massDrySamplePlusPanG === 'number' &&
    typeof p.massOfPanG === 'number'
  ) {
    const legacyDry = p.massDrySamplePlusPanG - p.massOfPanG - nz(p.massExtraG);
    if (legacyDry > 0) massDry = legacyDry;
  }

  // Water content: (wet − dry) / dry × 100
  let waterContentPct: number | null = null;
  if (initialWetMass !== null && massDry !== null && massDry > 0 && initialWetMass >= massDry) {
    waterContentPct = ((initialWetMass - massDry) / massDry) * 100;
  }

  // Bulk & dry density — both keyed off the initial volume.
  // Bulk uses wet mass; dry uses dry mass directly (no longer back-derived
  // from bulk × (1+w) — this matches the lab spreadsheet's
  // "Dry Density = Dry Mass / Volume" formula referenced in the ticket).
  let bulkMgPerM3: number | null = null;
  let dryMgPerM3: number | null = null;
  if (V !== null && V > 0) {
    if (initialWetMass !== null && initialWetMass > 0) {
      bulkMgPerM3 = gPerMm3ToMgPerM3(initialWetMass / V);
    }
    if (massDry !== null && massDry > 0) {
      dryMgPerM3 = gPerMm3ToMgPerM3(massDry / V);
    }
  }

  // Void ratio: e = (Gs × ρ_w / ρ_d) − 1
  let voidRatio: number | null = null;
  if (typeof p.specificGravityGs === 'number' && p.specificGravityGs > 0 && dryMgPerM3 !== null && dryMgPerM3 > 0) {
    voidRatio = (p.specificGravityGs * 1.0) / dryMgPerM3 - 1; // ρ_w = 1 Mg/m³
  }

  // Degree of saturation: S_r = w × Gs / e × 100 (clamped to ≤ 100)
  let degreeOfSatPct: number | null = null;
  if (
    waterContentPct !== null &&
    typeof p.specificGravityGs === 'number' &&
    voidRatio !== null &&
    voidRatio > 0
  ) {
    degreeOfSatPct = clamp((waterContentPct / 100) * p.specificGravityGs / voidRatio * 100, 0, 100);
  }

  return {
    avgDiameterMm: safe(avgD),
    avgLengthMm: safe(avgL),
    calcDiameterMm: safe(calcD),
    reportedDiameterMm: safe(reportedD),
    initialVolumeMm3: safe(V),
    initialWetMassG: safe(initialWetMass),
    bulkDensityMgPerM3: safe(bulkMgPerM3),
    dryDensityMgPerM3: safe(dryMgPerM3),
    waterContentPct: safe(waterContentPct),
    voidRatio: safe(voidRatio),
    degreeOfSatPct: safe(degreeOfSatPct),
    massDryG: safe(massDry),
  };
}

// ============================================================================
// 2. Consolidation-phase row transform
// ============================================================================

export interface ConsolidationRowRaw {
  timeSinceStartOfTestS: number | null;
  backVolumeMm3: number | null;
  axialDisplacementMm: number | null;
}

export interface ConsolidationRowComputed {
  consolidationTimeS: number | null;
  correctedBackVolumeMm3: number | null;
  axialDisplacementMm: number | null;
}

export function computeConsolidationRow(
  raw: ConsolidationRowRaw,
  firstRowInPhase: ConsolidationRowRaw,
  endOfSaturationDeformationMm: number,
): ConsolidationRowComputed {
  return {
    consolidationTimeS:
      raw.timeSinceStartOfTestS !== null && firstRowInPhase.timeSinceStartOfTestS !== null
        ? raw.timeSinceStartOfTestS - firstRowInPhase.timeSinceStartOfTestS
        : null,
    correctedBackVolumeMm3: raw.backVolumeMm3 !== null ? -raw.backVolumeMm3 : null,
    axialDisplacementMm:
      raw.axialDisplacementMm !== null
        ? raw.axialDisplacementMm - endOfSaturationDeformationMm
        : null,
  };
}

// ============================================================================
// 3. Post-consolidation properties
// ============================================================================

export type AreaMethod = 'A' | 'B' | 'Both';

export interface PostConsolidationInput {
  initialHeightMm: number;
  initialVolumeMm3: number;
  initialDryMassG: number;
  consolidationRowsRaw: ConsolidationRowRaw[];  // need first & last of phase
  areaMethod: AreaMethod;
  specificGravityGs: number;
  /** When method B is selected, operator may supply post-cons w%; if null we assume same as initial. */
  postConsWaterContentInputPct: number | null;
  initialWaterContentPct: number | null;
  /**
   * Axial deformation (mm) accumulated during saturation, BEFORE consolidation
   * (= EndOfSaturationDeformation − InitialDeformation). Subtracted from the
   * post-cons height on top of the consolidation delta — the delta cancels the
   * consolidation rows' shared LVDT origin and never sees it. Matches the lab
   * spreadsheet's Lc (BUG-2026-0103). Optional, defaults to 0.
   */
  saturationDeformationMm?: number | null;
}

export interface PostConsolidationOutput {
  heightMm: number | null;
  diameterMmMethodA: number | null;
  diameterMmMethodB: number | null;
  reportedDiameterMm: number | null;
  /** Area in m² for downstream shear-stress calc — picks method per `areaMethod`. */
  reportedAreaM2: number | null;
  areaMethodAM2: number | null;
  areaMethodBM2: number | null;
  volumeMm3: number | null;
  /**
   * Post-cons volume (mm³) consistent with the SELECTED area method —
   * reportedArea × height (BUG-2026-0108). Mirrors the backend: this is the
   * volume the densities / void ratio / S_r are derived from.
   */
  reportedVolumeMm3: number | null;
  bulkDensityMgPerM3: number | null;
  dryDensityMgPerM3: number | null;
  waterContentPct: number | null;
  voidRatio: number | null;
  degreeOfSatPct: number | null;
}

export function computePostConsolidationProperties(p: PostConsolidationInput): PostConsolidationOutput {
  const rows = p.consolidationRowsRaw;
  if (rows.length < 2) {
    return {
      heightMm: null,
      diameterMmMethodA: null,
      diameterMmMethodB: null,
      reportedDiameterMm: null,
      reportedAreaM2: null,
      areaMethodAM2: null,
      areaMethodBM2: null,
      volumeMm3: null,
      reportedVolumeMm3: null,
      bulkDensityMgPerM3: null,
      dryDensityMgPerM3: null,
      waterContentPct: null,
      voidRatio: null,
      degreeOfSatPct: null,
    };
  }

  const first = rows[0];
  const last = rows[rows.length - 1];

  // Volume change during consolidation (mm³) — water leaving the sample
  // corresponds to positive corrected ΔV (back volume × −1). The corrected
  // ΔV is the sample's volume change (negative = compression).
  const deltaBackRaw = nz(last.backVolumeMm3) - nz(first.backVolumeMm3);
  const deltaVSample = -deltaBackRaw; // sample loses what the syringe gains
  const volumeMm3 = p.initialVolumeMm3 + deltaVSample;

  // Height change — axial displacement increases as sample compresses.
  // Drop BOTH the saturation deformation (before consolidation) and the
  // consolidation-phase delta; the delta alone cancels the shared LVDT origin
  // and never sees the saturation deformation (BUG-2026-0103).
  const deltaH = nz(last.axialDisplacementMm) - nz(first.axialDisplacementMm);
  const heightMm = p.initialHeightMm - nz(p.saturationDeformationMm) - deltaH;

  // Method A: from V_pc and H_pc
  let dA: number | null = null;
  let aAm2: number | null = null;
  if (volumeMm3 > 0 && heightMm > 0) {
    dA = Math.sqrt((4 * volumeMm3) / (Math.PI * heightMm));
    aAm2 = (Math.PI / 4) * dA * dA * 1e-6; // mm² → m²
  }

  // Method B: from water-content change (assumes S_r = 100% after back-saturation)
  // V_solids = mass_dry / (Gs × ρ_w); V_water = w × mass_dry / 100 / ρ_w
  // V_total = V_solids + V_water → D_pc from V_total and H_pc
  const wPc = p.postConsWaterContentInputPct ?? p.initialWaterContentPct ?? null;
  let dB: number | null = null;
  let aBm2: number | null = null;
  if (
    typeof wPc === 'number' &&
    p.specificGravityGs > 0 &&
    p.initialDryMassG > 0 &&
    heightMm > 0
  ) {
    const vSolids = p.initialDryMassG / (p.specificGravityGs * WATER_DENSITY_G_PER_MM3);
    const vWater = (wPc / 100) * p.initialDryMassG / WATER_DENSITY_G_PER_MM3;
    const vTotal = vSolids + vWater;
    dB = Math.sqrt((4 * vTotal) / (Math.PI * heightMm));
    aBm2 = (Math.PI / 4) * dB * dB * 1e-6;
  }

  let reportedD: number | null = null;
  let reportedArea: number | null = null;
  if (p.areaMethod === 'A') {
    reportedD = dA;
    reportedArea = aAm2;
  } else if (p.areaMethod === 'B') {
    reportedD = dB;
    reportedArea = aBm2;
  } else if (p.areaMethod === 'Both') {
    // BGC convention: when Both, use the average area
    if (aAm2 !== null && aBm2 !== null) {
      reportedArea = (aAm2 + aBm2) / 2;
      reportedD = Math.sqrt((4 * reportedArea * 1e6) / Math.PI);
    } else {
      reportedArea = aAm2 ?? aBm2 ?? null;
      reportedD = dA ?? dB ?? null;
    }
  }

  // Volume consistent with the reported (method-selected) area — BUG-2026-0108.
  // The back-volume `volumeMm3` is Method A's basis; when the operator selects
  // Method B it is precisely because the back-volume channel is untrustworthy,
  // so the densities (and the void ratio / S_r derived from them) must use the
  // method-consistent volume, not the back-volume one. reportedArea is in m²
  // (×1e6 → mm²); for Method A this product equals `volumeMm3` exactly.
  const reportedVolumeMm3 =
    reportedArea !== null && reportedArea > 0 && heightMm > 0
      ? reportedArea * 1e6 * heightMm
      : volumeMm3 > 0
        ? volumeMm3
        : null;

  // Derived bulk/dry density, void ratio, degree-of-sat at post-cons state
  const massWet = p.initialDryMassG * (1 + nz(wPc) / 100);
  const bulkPc =
    reportedVolumeMm3 !== null ? gPerMm3ToMgPerM3(massWet / reportedVolumeMm3) : null;
  const dryPc =
    reportedVolumeMm3 !== null ? gPerMm3ToMgPerM3(p.initialDryMassG / reportedVolumeMm3) : null;
  const ePc =
    dryPc !== null && dryPc > 0 ? (p.specificGravityGs * 1.0) / dryPc - 1 : null;
  const srPc =
    typeof wPc === 'number' && ePc !== null && ePc > 0
      ? clamp((wPc / 100) * p.specificGravityGs / ePc * 100, 0, 100)
      : null;

  return {
    heightMm: safe(heightMm),
    diameterMmMethodA: safe(dA),
    diameterMmMethodB: safe(dB),
    reportedDiameterMm: safe(reportedD),
    reportedAreaM2: safe(reportedArea),
    areaMethodAM2: safe(aAm2),
    areaMethodBM2: safe(aBm2),
    volumeMm3: safe(volumeMm3),
    reportedVolumeMm3: safe(reportedVolumeMm3),
    bulkDensityMgPerM3: safe(bulkPc),
    dryDensityMgPerM3: safe(dryPc),
    waterContentPct: safe(wPc),
    voidRatio: safe(ePc),
    degreeOfSatPct: safe(srPc),
  };
}

// ============================================================================
// 4. Final properties (post-test, tin-based moisture measurement)
// ============================================================================

export interface FinalPropertiesInput {
  massOfTinG: number | null;
  finalWetMassPlusTinG: number | null;
  finalDryMassPlusTin1G: number | null;
  specificGravityGs: number | null;
  postConsVolumeMm3: number | null;
  initialVolumeMm3: number | null;
}

export interface FinalPropertiesOutput {
  finalDryMassG: number | null;
  finalWetMassG: number | null;
  waterContentPct: number | null;
  bulkDensityMgPerM3: number | null;
  dryDensityMgPerM3: number | null;
  voidRatio: number | null;
  degreeOfSatPct: number | null;
}

/**
 * Final dry mass (g) from the pan pair — pure helper extracted from
 * `computeFinalProperties` so callers can derive ONLY the dry mass without
 * needing a volume (e.g. the initial-properties path that needs the dry mass
 * to back-calc water content + dry density per BUG-2026-0053). Returns null
 * when either weighing is missing. Single-reading per BUG-2026-0054.
 */
export function computeFinalDryMassG(
  massOfTinG: number | null,
  finalDryMassPlusTin1G: number | null,
): number | null {
  if (typeof finalDryMassPlusTin1G !== 'number') return null;
  return finalDryMassPlusTin1G - nz(massOfTinG);
}

export function computeFinalProperties(p: FinalPropertiesInput): FinalPropertiesOutput {
  const tin = nz(p.massOfTinG);
  const dryMass = computeFinalDryMassG(p.massOfTinG, p.finalDryMassPlusTin1G);
  const wetMass = typeof p.finalWetMassPlusTinG === 'number' ? p.finalWetMassPlusTinG - tin : null;

  let waterContentPct: number | null = null;
  if (dryMass !== null && dryMass > 0 && wetMass !== null && wetMass >= dryMass) {
    waterContentPct = ((wetMass - dryMass) / dryMass) * 100;
  }

  const volume = (p.postConsVolumeMm3 && p.postConsVolumeMm3 > 0)
    ? p.postConsVolumeMm3
    : (p.initialVolumeMm3 && p.initialVolumeMm3 > 0 ? p.initialVolumeMm3 : null);

  let bulkMgPerM3: number | null = null;
  let dryMgPerM3: number | null = null;
  if (wetMass !== null && wetMass > 0 && volume !== null) {
    bulkMgPerM3 = gPerMm3ToMgPerM3(wetMass / volume);
    if (waterContentPct !== null) {
      dryMgPerM3 = bulkMgPerM3 / (1 + waterContentPct / 100);
    }
  } else if (dryMass !== null && dryMass > 0 && volume !== null && waterContentPct === null) {
    dryMgPerM3 = gPerMm3ToMgPerM3(dryMass / volume);
  }

  let voidRatio: number | null = null;
  if (typeof p.specificGravityGs === 'number' && p.specificGravityGs > 0 && dryMgPerM3 !== null && dryMgPerM3 > 0) {
    voidRatio = (p.specificGravityGs * 1.0) / dryMgPerM3 - 1;
  }

  let degreeOfSatPct: number | null = null;
  if (
    waterContentPct !== null &&
    typeof p.specificGravityGs === 'number' &&
    voidRatio !== null &&
    voidRatio > 0
  ) {
    degreeOfSatPct = clamp((waterContentPct / 100) * p.specificGravityGs / voidRatio * 100, 0, 100);
  }

  return {
    finalDryMassG: safe(dryMass),
    finalWetMassG: safe(wetMass),
    waterContentPct: safe(waterContentPct),
    bulkDensityMgPerM3: safe(bulkMgPerM3),
    dryDensityMgPerM3: safe(dryMgPerM3),
    voidRatio: safe(voidRatio),
    degreeOfSatPct: safe(degreeOfSatPct),
  };
}

// ============================================================================
// 5. Post-consolidation volume reconstruction (frontend-only helper)
// ============================================================================

/**
 * Reconstruct the post-consolidation specimen volume (mm³) from the two
 * persisted caches the backend exposes: reported post-cons diameter (cm) and
 * post-cons height (cm), via V = π/4·D²·H.
 *
 * Exact by construction — `computePostConsolidationProperties` back-solves the
 * reported diameter from the method-selected area (D_pc = √(4·A/π)), and the
 * density basis is reportedVolume = A × H_pc, so π/4·D_pc²·H_pc ≡ that basis.
 *
 * BUG-2026-0107: the Parameters tab's Final-Properties live preview needs the
 * post-cons volume to match the backend's density basis; without it the panel
 * fell back to the initial volume and disagreed with the SAMPLE PROPERTIES
 * table / PDF.
 *
 * BUG-2026-0108: the basis must be the REPORTED (method-consistent) geometry.
 * Do NOT reconstruct from the Method-A area cache — on Method-B tests the
 * back-volume-derived Method-A geometry diverges from the saturation-based
 * volume the densities / void ratio / S_r are actually computed from.
 */
export function reconstructPostConsVolumeMm3(
  postConsDiameterCm: number | null | undefined,
  postConsHeightCm: number | null | undefined,
): number | null {
  if (typeof postConsDiameterCm !== 'number' || !Number.isFinite(postConsDiameterCm) || postConsDiameterCm <= 0) return null;
  if (typeof postConsHeightCm !== 'number' || !Number.isFinite(postConsHeightCm) || postConsHeightCm <= 0) return null;
  // cm → mm (×10) on both axes ⇒ mm³
  const dMm = postConsDiameterCm * 10;
  const hMm = postConsHeightCm * 10;
  return (Math.PI / 4) * dMm * dMm * hMm;
}

// Exposed for the verify script (kept private otherwise).
export const _internalsForVerify = {
  avg,
  clamp,
  nz,
  safe,
  gPerMm3ToMgPerM3,
  WATER_DENSITY_G_PER_MM3,
};
